import { useState, useCallback } from 'react'

export interface ValidationRule {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
  min?: number
  max?: number
  custom?: (value: any) => string | null
}

export interface ValidationRules {
  [field: string]: ValidationRule
}

export interface ValidationErrors {
  [field: string]: string
}

export const useFormValidation = (rules: ValidationRules) => {
  const [errors, setErrors] = useState<ValidationErrors>({})

  const validateField = useCallback((name: string, value: any): string | null => {
    const rule = rules[name]
    if (!rule) return null

    // Required validation
    if (rule.required && (!value || value.toString().trim() === '')) {
      return 'Este campo é obrigatório'
    }

    // Skip other validations if value is empty and not required
    if (!value || value.toString().trim() === '') {
      return null
    }

    // String validations
    if (typeof value === 'string') {
      if (rule.minLength && value.length < rule.minLength) {
        return `Deve ter pelo menos ${rule.minLength} caracteres`
      }
      if (rule.maxLength && value.length > rule.maxLength) {
        return `Deve ter no máximo ${rule.maxLength} caracteres`
      }
      if (rule.pattern && !rule.pattern.test(value)) {
        return 'Formato inválido'
      }
    }

    // Number validations
    if (typeof value === 'number' || !isNaN(Number(value))) {
      const numValue = Number(value)
      if (rule.min !== undefined && numValue < rule.min) {
        return `Deve ser maior ou igual a ${rule.min}`
      }
      if (rule.max !== undefined && numValue > rule.max) {
        return `Deve ser menor ou igual a ${rule.max}`
      }
    }

    // Custom validation
    if (rule.custom) {
      return rule.custom(value)
    }

    return null
  }, [rules])

  const validateForm = useCallback((data: any): boolean => {
    const newErrors: ValidationErrors = {}
    let isValid = true

    Object.keys(rules).forEach(field => {
      const error = validateField(field, data[field])
      if (error) {
        newErrors[field] = error
        isValid = false
      }
    })

    setErrors(newErrors)
    return isValid
  }, [rules, validateField])

  const validateSingleField = useCallback((name: string, value: any) => {
    const error = validateField(name, value)
    setErrors(prev => ({
      ...prev,
      [name]: error || ''
    }))
    return !error
  }, [validateField])

  const clearErrors = useCallback(() => {
    setErrors({})
  }, [])

  const clearFieldError = useCallback((field: string) => {
    setErrors(prev => ({
      ...prev,
      [field]: ''
    }))
  }, [])

  return {
    errors,
    validateForm,
    validateSingleField,
    clearErrors,
    clearFieldError,
    hasErrors: Object.values(errors).some(error => error !== '')
  }
}

// Predefined validation rules for common cases
export const validationRules = {
  required: { required: true },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  },
  password: {
    required: true,
    minLength: 6
  },
  positiveNumber: {
    required: true,
    min: 0,
    custom: (value: any) => {
      if (isNaN(Number(value))) {
        return 'Deve ser um número válido'
      }
      return null
    }
  },
  accountName: {
    required: true,
    minLength: 2,
    maxLength: 100
  },
  bankName: {
    required: true,
    minLength: 2,
    maxLength: 50
  },
  categoryName: {
    required: true,
    minLength: 2,
    maxLength: 50
  },
  description: {
    maxLength: 255
  },
  amount: {
    required: true,
    min: 0.01,
    custom: (value: any) => {
      const num = Number(value)
      if (isNaN(num)) {
        return 'Deve ser um número válido'
      }
      if (num <= 0) {
        return 'O valor deve ser maior que zero'
      }
      return null
    }
  }
}